/**
 * Zod 4 schemas for every data contract in docs/test-plan.md §1.
 * Lengths are integer 0.1 mm ticks unless a field name or comment says mm.
 * Every file-level contract carries `schemaVersion`.
 */
import { z } from 'zod';
import { ConstructionProfileSchema } from './profiles';
import {
  AssemblyOpeningSchema, AssemblyPartSchema, BoardAssemblySchema, IssueSchema as AssemblyIssueSchema, PlacementSchema as AssemblyPlacementSchema,
  StockFitSchema, ValidationSchema as AssemblyValidationSchema,
} from '../families/assembly';
import { FitResultSchema } from '../families/fit-schema';

export const SCHEMA_VERSION = 1 as const;

const isoDate = z.string().min(1);
const hex64 = z.string().regex(/^[0-9a-f]{64}$/, 'Expected a SHA-256 hex digest');
const revisionId = z.string().regex(/^r\d+$/, 'Expected a revision id like r3');

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

export const FieldStatusSchema = z.enum(['proposed', 'published', 'confirmed', 'edited', 'unresolved']);
export type FieldStatus = z.infer<typeof FieldStatusSchema>;
export const FieldSourceSchema = z.enum(['image', 'text', 'manual']);
export type FieldSource = z.infer<typeof FieldSourceSchema>;
export const FieldKeySchema = z.enum(['family', 'W', 'H', 'D', 'shelfCount', 'even', 'openings', 'openingRatios', 'stockThickness', 'material', 'finish']);
export type FieldKey = z.infer<typeof FieldKeySchema>;

export const FieldEvidenceSchema = z.looseObject({
  quote: z.string().optional(),
  span: z.tuple([z.int(), z.int()]).optional(),
  note: z.string().optional(),
  imageId: z.string().optional(),
});

export const FieldHistoryEntrySchema = z.object({
  status: FieldStatusSchema,
  value: z.unknown(),
  display: z.string(),
  at: isoDate,
  by: z.string(),
  note: z.string().optional(),
});
export type FieldHistoryEntry = z.infer<typeof FieldHistoryEntrySchema>;

export const FieldRecordSchema = z.object({
  key: z.string().min(1),
  label: z.string(),
  critical: z.boolean(),
  value: z.unknown(),
  display: z.string(),
  status: FieldStatusSchema,
  source: FieldSourceSchema,
  evidence: FieldEvidenceSchema.optional(),
  note: z.string().optional(),
  history: z.array(FieldHistoryEntrySchema),
});
export type FieldRecord = z.infer<typeof FieldRecordSchema>;

export const FieldsSchema = z.record(z.string(), FieldRecordSchema);
export type Fields = Record<string, FieldRecord>;

// ---------------------------------------------------------------------------
// DesignInput (core `design`)
// ---------------------------------------------------------------------------

export const AppearanceSchema = z.object({
  name: z.string(),
  props: z.boolean(),
  staging: z.string(),
  /** Look (plan §3 step 4): finish variant from the kit (oiled · satin · matt). Absent = the stock's own finish. */
  finish: z.enum(['oiled', 'satin', 'matt']).optional(),
  /** Look: show fixings (screws, dowels, brackets) on the model. */
  hardware: z.boolean().optional(),
});
export type Appearance = z.infer<typeof AppearanceSchema>;

export const DesignInputSchema = z.object({
  family: z.string().min(1),
  W: z.int(),
  H: z.int(),
  D: z.int(),
  n: z.int().min(0).max(50),
  even: z.boolean(),
  /** Clear heights, ticks, bottom-up, n + 1 of them. */
  openings: z.array(z.int()),
  stockProfileId: z.string().min(1),
  constructionProfileId: z.string().min(1),
  appearance: AppearanceSchema,
  fields: FieldsSchema.optional(),
  // --- grid-bookcase widening (plan §4.6). All optional: absent = today's one-bay bookcase,
  // so a one-bay design with an overlay back and no plinth is exactly the first slice's DesignInput.
  /** Bays side by side, 1–4 (absent = 1). `n`, `even`, `openings` above are bay 1. */
  bays: z.int().min(1).max(8).optional(),
  /** Clear width of each bay, ticks, left to right (only when bays > 1). */
  bayWidths: z.array(z.int()).optional(),
  /** Bays 2…N: their own shelf count, Even and openings (bay 1 is the top level). */
  bayLayouts: z.array(z.object({ n: z.int().min(0).max(50), even: z.boolean(), openings: z.array(z.int()) })).optional(),
  /** Back panel: overlay (default), rebated into the carcass, or none. */
  back: z.enum(['none', 'overlay', 'rebated']).optional(),
  /** Plinth height, ticks (absent or 0 = none). */
  plinth: z.int().min(0).optional(),
  /** How fixed shelves meet the verticals (default screwed-butt). */
  joinery: z.enum(['screwed-butt', 'housed-dado']).optional(),
  /** What the case stands on (FND-08, src/families/base.ts): absent = the plinth above, or the floor. */
  base: z.enum(['none', 'plinth', 'feet', 'sides']).optional(),
  /** Plinth set back from the front edge, ticks (absent = flush). */
  plinthSetback: z.int().min(0).optional(),
  /** Levelling-foot height, ticks, when base is feet (absent = 20 mm). */
  feetHeight: z.int().min(0).optional(),
  /**
   * Doors and drawers over the openings (src/families/fronts.ts `FrontSpec`); absent or [] = none. Validated by
   * the fronts module, so an older reader that does not know a field ignores it.
   */
  fronts: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Gap under the raised bottom, ticks, when base is sides (absent = 60 mm). */
  toeHeight: z.int().min(0).optional(),
  /** Outer sides as panels (absent) or vertical slats (FND-13); slat width and gap in ticks. */
  sideStyle: z.enum(['panel', 'slatted']).optional(),
  slatWidth: z.int().min(0).optional(),
  slatGap: z.int().min(0).optional(),
  // --- families 2–4 (plan §4.7): every other family keeps its own parameter block here, as
  // counts, booleans and ratios (W, H, D and the stock stay top level). Absent on grid designs;
  // `n`, `even` and `openings` are then 0, true and [] and unused.
  params: z.record(z.string(), z.unknown()).optional(),
});
export type Design = z.infer<typeof DesignInputSchema>;
export type BayLayout = NonNullable<Design['bayLayouts']>[number];

// ---------------------------------------------------------------------------
// BuildModel (core `build`)
// ---------------------------------------------------------------------------

export const PartRoleSchema = z.string().min(1);
export type PartRole = z.infer<typeof PartRoleSchema>;

export const PlacementSchema = AssemblyPlacementSchema;
export type Placement = z.infer<typeof PlacementSchema>;

/** A part of a BoardAssembly (plan §4.2). */
export const PartSchema = AssemblyPartSchema;
export type Part = z.infer<typeof PartSchema>;

/** The grid family's opening row: the substrate's opening plus the first slice's fields. */
export const OpeningRowSchema = AssemblyOpeningSchema.extend({
  index: z.int(),
  number: z.int(),
  clear: z.number(),
  bottomDatum: z.number(),
  topDatum: z.number(),
  /** 0-based bay; absent on a one-bay piece. */
  bay: z.int().optional(),
});
export type OpeningRow = z.infer<typeof OpeningRowSchema>;

export const IssueSchema = AssemblyIssueSchema;
export type Issue = z.infer<typeof IssueSchema>;
export const ValidationSchema = AssemblyValidationSchema;
export type Validation = z.infer<typeof ValidationSchema>;

const limit = z.object({ min: z.number(), max: z.number(), label: z.string() });
export const LimitsSchema = z.object({
  W: limit, H: limit, D: limit, n: limit,
  minOpening: z.number(), maxShelfSpanWarning: z.number(), antiTipAbove: z.number(),
});

export const SheetFitSchema = StockFitSchema;
export type SheetFit = z.infer<typeof SheetFitSchema>;

/** Per bay (grid family): clear width and the openings sum rule. */
export const BaySummarySchema = z.object({
  index: z.int(),
  clearWidth: z.number(),
  n: z.int(),
  available: z.number(),
  openingsSum: z.number(),
  remainder: z.number(),
});
export type BaySummary = z.infer<typeof BaySummarySchema>;

/**
 * BuildModel: the grid family's BoardAssembly plus the first slice's fields (bay 1's
 * openings sum rule, shelf datums, sheet fit). Everything downstream reads the substrate fields.
 */
export const BuildModelSchema = BoardAssemblySchema.extend({
  family: z.string(),
  design: DesignInputSchema,
  /** The carcass stock profile (sheet or board). */
  stock: z.looseObject({ id: z.string(), version: z.string() }),
  construction: ConstructionProfileSchema,
  thickness: z.object({ carcass: z.number(), back: z.number() }),
  openings: z.array(OpeningRowSchema),
  /** Bay 1 shelf undersides from the floor, mm, bottom-up (all bays: `datums`). */
  shelfDatums: z.array(z.number()),
  available: z.number(),
  availableMm: z.number(),
  openingsSum: z.number(),
  /** Ticks left to place in bay 1 (negative = too much). */
  remainder: z.number(),
  bays: z.array(BaySummarySchema),
  envelope: z.object({ W: z.number(), H: z.number(), D: z.number(), limits: LimitsSchema }),
  sheet: z.object({ length: z.number(), width: z.number(), usableLength: z.number(), usableWidth: z.number() }),
  sheetFit: z.array(SheetFitSchema),
  panelCount: z.int(),
});
export type BuildModel = z.infer<typeof BuildModelSchema>;

// ---------------------------------------------------------------------------
// Revisions, working state, autosave
// ---------------------------------------------------------------------------

export const ChangeKindSchema = z.enum(['manufacturing', 'appearance', 'layout', 'none']);
export type ChangeKind = z.infer<typeof ChangeKindSchema>;
export const RevisionKindSchema = z.enum(['proposal', 'manual', 'manufacturing', 'appearance', 'layout']);
export type RevisionKind = z.infer<typeof RevisionKindSchema>;

export const RevisionSchema = z.object({
  id: revisionId,
  label: z.string(),
  kind: RevisionKindSchema,
  design: DesignInputSchema,
  fields: FieldsSchema,
  manufacturingHash: hex64,
  appearanceHash: hex64,
  /** v2 contract identity (src/families/contract.ts) at commit time; absent on revisions made before it (treated as unchecked). */
  assemblyHash: hex64.optional(),
  createdAt: isoDate,
  createdBy: z.string(),
  parentRevisionId: revisionId.nullable().optional(),
  note: z.string().optional(),
});
export type Revision = z.infer<typeof RevisionSchema>;

/** One undo/redo entry. `design`/`fields` hold the state to return to (absent in summaries). */
export const HistoryEntrySchema = z.object({
  label: z.string(),
  change: ChangeKindSchema,
  design: DesignInputSchema.optional(),
  fields: FieldsSchema.optional(),
});
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;

export const WorkingSchema = z.object({
  parentRevisionId: revisionId.nullable(),
  design: DesignInputSchema,
  fields: FieldsSchema,
  dirty: z.boolean(),
  undoStack: z.array(HistoryEntrySchema),
  redoStack: z.array(HistoryEntrySchema),
});
export type Working = z.infer<typeof WorkingSchema>;

export const ExportKindSchema = z.enum(['csv', 'json', 'print']);
export type ExportKind = z.infer<typeof ExportKindSchema>;
export const ExportRecordSchema = z.object({
  at: isoDate,
  kind: ExportKindSchema,
  revisionId,
  manufacturingHash: hex64,
  appearanceHash: hex64,
  filename: z.string().min(1),
});
export type ExportRecord = z.infer<typeof ExportRecordSchema>;

export const AutosaveSnapshotSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  savedAt: isoDate,
  state: z.object({
    design: DesignInputSchema,
    fields: FieldsSchema,
    revisions: z.array(RevisionSchema),
    currentRevisionId: revisionId.nullable(),
    undoStack: z.array(HistoryEntrySchema),
    redoStack: z.array(HistoryEntrySchema),
    exports: z.array(ExportRecordSchema).optional(),
  }),
});
export type AutosaveSnapshot = z.infer<typeof AutosaveSnapshotSchema>;

// ---------------------------------------------------------------------------
// Finish artefacts (retired with runtime Blender, plan §6; kept only for the kit agent's
// scripts/validate-finish.mjs until it is retired)
// ---------------------------------------------------------------------------

export const CheckStatusSchema = z.enum(['pending', 'running', 'passed', 'failed']);

/** @deprecated The FinishJob contract is retired (plan §8.2); kept for scripts/validate-finish.mjs only. */
export const ArtifactManifestSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  fingerprint: hex64,
  files: z.array(z.object({ path: z.string().min(1), sha256: hex64, bytes: z.int().nonnegative() })),
  validation: z.object({
    passed: z.boolean(),
    checks: z.array(z.object({ id: z.string(), label: z.string(), status: CheckStatusSchema, detail: z.string().optional() })),
  }),
  timings: z.record(z.string(), z.number()),
  blenderVersion: z.string(),
  pipelineVersion: z.string(),
  worker: z.enum(['remote', 'local']),
});
export type ArtifactManifest = z.infer<typeof ArtifactManifestSchema>;

// ---------------------------------------------------------------------------
// Pack
// ---------------------------------------------------------------------------

export const PackStatusSchema = z.enum(['draft', 'invalid']);
export type PackStatus = z.infer<typeof PackStatusSchema>;

export const PackMetadataSchema = z.object({
  revisionId: revisionId.nullable(),
  manufacturingHash: hex64,
  appearanceHash: hex64,
  status: PackStatusSchema,
  provisionalProfile: z.boolean(),
  generatedAt: isoDate,
  stockId: z.string(),
  measuredThicknessMm: z.number(),
  profileId: z.string(),
  profileVersion: z.string(),
  profileReviewer: z.string().nullable().optional(),
  profileReviewedAt: z.string().nullable().optional(),
  generatorVersion: z.string(),
  // families programme (test-plan §1a PackMetadata revised)
  familyId: z.string().optional(),
  familyVersion: z.string().optional(),
  jointsProfileId: z.string().optional(),
  jointsVersion: z.string().optional(),
  selfAuthored: z.boolean().optional(),
});
export type PackMetadata = z.infer<typeof PackMetadataSchema>;

// ---------------------------------------------------------------------------
// Intake
// ---------------------------------------------------------------------------

export const ReferenceEvidenceSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  images: z.array(z.object({ id: z.string().min(1), path: z.string().min(1), sha256: hex64, rightsBasis: z.string().min(1) })),
  sourceUrl: z.string(),
  pastedText: z.string().optional(),
  publishedFacts: z.array(
    z.object({
      kind: z.string().min(1),
      value: z.union([z.number(), z.string()]),
      unit: z.string().nullable(),
      quotedText: z.string(),
      span: z.tuple([z.int().nonnegative(), z.int().nonnegative()]),
    }),
  ),
  imageEstimates: z.array(
    z.object({ kind: z.string().min(1), value: z.union([z.number(), z.string(), z.array(z.number())]), confidence: z.number().min(0).max(1), basis: z.string() }),
  ),
  unknowns: z.array(z.string()),
});
export type ReferenceEvidence = z.infer<typeof ReferenceEvidenceSchema>;

export const IntakeProposalSchema = z.object({
  family: z.string(),
  isSupported: z.boolean(),
  rejectionReason: z.string().optional(),
  detected: z.object({ family: z.string(), bays: z.int().nonnegative() }).optional(),
  shelfCount: z.int().min(0).nullable(),
  /** Bottom-up; ratios of the available clear height. */
  openings: z.array(z.object({ clearHeightRatio: z.number().min(0).max(1) })),
  evenSpacing: z.boolean().nullable(),
  recognisedFacts: z.array(
    z.object({ kind: z.string(), value: z.union([z.number(), z.string()]), unit: z.string().nullable(), source: z.enum(['text', 'image']) }),
  ),
  evidence: z.object({
    /** Normalised 0–1, y = 0 at the top of the image. */
    shelfLines: z.array(z.object({ partId: z.string(), y: z.number().min(0).max(1), band: z.number().min(0).max(1) })),
    carcass: z.object({ left: z.number(), right: z.number(), top: z.number(), bottom: z.number() }).nullable(),
    textSpans: z.array(z.object({ kind: z.string(), span: z.tuple([z.int().nonnegative(), z.int().nonnegative()]), text: z.string() })),
  }),
  evidenceNotes: z.array(z.string()),
});
export type IntakeProposal = z.infer<typeof IntakeProposalSchema>;

// ---------------------------------------------------------------------------
// Project (service file) and experiment handoff record
// ---------------------------------------------------------------------------

/**
 * An independent check of one revision's assembly (shelf-board-assembly plan Phase 3): the result of
 * pipeline/construction/construction/assembly_check.py, bound to the exact input bytes, the assembly and
 * manufacturing identities and the checker and policy versions. Service-owned, like exports.
 */
/** The checker whose receipts count (CHECKER_VERSION in assembly_check.py). */
export const ASSEMBLY_CHECKER_VERSION = 'assembly-check@1';

export const CheckReceiptSchema = z.object({
  revisionId,
  status: z.enum(['passed', 'failed']),
  assemblyHash: hex64,
  manufacturingHash: hex64,
  inputSha256: hex64,
  checkerVersion: z.string().min(1),
  policyVersion: z.string().min(1),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  at: isoDate,
});
export type CheckReceipt = z.infer<typeof CheckReceiptSchema>;

export const ProjectSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  name: z.string().min(1),
  reference: ReferenceEvidenceSchema.nullable(),
  analysis: z.object({
    status: z.enum(['none', 'running', 'review', 'done', 'manual', 'refused', 'error']),
    ranAt: isoDate.nullable(),
    proposalAt: isoDate.nullable(),
    /** The analysis result: a FitResult (families programme), or a first-slice IntakeProposal in older projects. */
    proposal: z.union([FitResultSchema, IntakeProposalSchema]).optional(),
    refusal: z.object({
      reason: z.string(),
      detected: z.object({ family: z.string(), bays: z.int().nonnegative() }).optional(),
      /** No-fit: the closest families, for "Set up by hand from a style" (never numbers). */
      candidates: z.array(z.object({ familyId: z.string(), confidence: z.number(), why: z.string() })).optional(),
    }).optional(),
    rawResponsePath: z.string().nullable(),
    usage: z.record(z.string(), z.number()).nullable(),
  }),
  working: WorkingSchema,
  revisions: z.array(RevisionSchema),
  /** Retired with Finish (plan §8.2); older project files carry an empty list. */
  jobs: z.array(z.unknown()).optional(),
  exports: z.array(ExportRecordSchema),
  /** Independent check receipts (service-owned; absent in projects saved before Phase 3). */
  checks: z.array(CheckReceiptSchema).optional(),
  savedAt: isoDate,
});
export type Project = z.infer<typeof ProjectSchema>;

export const HandoffRecordSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  revisionId,
  manufacturingHash: hex64,
  packFiles: z.array(z.string().min(1)),
  handedOverAt: isoDate,
  channel: z.string().min(1),
  questions: z.array(z.object({ at: isoDate, text: z.string(), answeredBy: z.string().nullable(), answer: z.string().nullable() })),
  corrections: z.array(z.object({ at: isoDate, text: z.string(), resultingRevisionId: revisionId.optional(), resultingHash: hex64.optional() })),
  goAhead: z.object({ at: isoDate, hash: hex64, by: z.string().min(1) }).optional(),
});
export type HandoffRecord = z.infer<typeof HandoffRecordSchema>;

